import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Alert, MenuItem, FormControlLabel, Switch, Table, TableHead,
  TableRow, TableCell, TableBody, IconButton, Tooltip, Chip, CircularProgress, InputAdornment, Grid, Skeleton,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { settingsApi } from '../services/api';
import ConfirmDialog from './common/ConfirmDialog';
import { ErrorState } from './common/States';
import { apiError, formatDate, mskToday } from '../utils/format';
import { marketplaceMeta, tokens } from '../theme';

const REGIMES = [
  { value: 'usn_income', label: 'УСН «Доходы»', rate: '6' },
  { value: 'usn_income_expense', label: 'УСН «Доходы минус расходы»', rate: '15' },
  { value: 'osno', label: 'ОСНО', rate: '25' },
  { value: 'ausn', label: 'АУСН', rate: '8' },
  { value: 'npd', label: 'НПД (самозанятый)', rate: '6' },
  { value: 'other', label: 'Другой режим', rate: '' },
];
const BASES = [
  { value: 'retail_amount', label: 'Выручка по цене продавца' },
  { value: 'for_pay', label: 'К перечислению за товар' },
];
const VAT_RATES = ['0', '5', '7', '10', '20', '22'];
const regimeLabel = (v) => REGIMES.find((r) => r.value === v)?.label || v;
const pct = (v) => `${Number(v).toLocaleString('ru-RU')}%`;

const emptyForm = () => ({ regime: 'usn_income', rate: '6', income_base: 'retail_amount', vat_payer: false, vat_rate: '5', valid_from: `${mskToday().slice(0, 4)}-01-01`, note: '' });

// Tax regime and VAT of one marketplace. Values apply from a date, so changing them today does not
// rewrite past periods unless an earlier date is chosen.
const TaxesPanel = ({ marketplace, readOnly = false }) => {
  const meta = marketplaceMeta(marketplace);
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await settingsApi.taxes(marketplace);
      setItems(data);
      setError('');
    } catch (e) {
      setError(apiError(e));
    }
  }, [marketplace]);
  useEffect(() => { setItems(null); load(); }, [load]);

  // start the form from the setting in force, so a change is an edit, not a retype
  useEffect(() => {
    const today = mskToday();
    const cur = items?.find((t) => t.valid_from <= today);
    if (cur) {
      setForm({
        regime: cur.regime, rate: String(Number(cur.rate)), income_base: cur.income_base || 'retail_amount', vat_payer: cur.vat_payer,
        vat_rate: cur.vat_rate ? String(Number(cur.vat_rate)) : '5', valid_from: today, note: '',
      });
    } else if (items) {
      setForm(emptyForm());
    }
  }, [items]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const rateNum = Number(String(form.rate).replace(',', '.'));
  const vatNum = Number(String(form.vat_rate).replace(',', '.'));
  const errors = {
    rate: form.rate === '' || Number.isNaN(rateNum) || rateNum < 0 || rateNum > 100 ? 'Число от 0 до 100' : '',
    vat_rate: form.vat_payer && (form.vat_rate === '' || Number.isNaN(vatNum) || vatNum < 0 || vatNum > 100) ? 'Число от 0 до 100' : '',
    valid_from: !form.valid_from ? 'Укажите дату' : '',
  };
  const valid = !errors.rate && !errors.vat_rate && !errors.valid_from;
  const computed = form.regime === 'usn_income';
  const today = mskToday();
  const current = items?.find((t) => t.valid_from <= today);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setMsg(null);
    try {
      await settingsApi.saveTax({
        marketplace, regime: form.regime, rate: String(form.rate).replace(',', '.'), income_base: computed ? form.income_base : '',
        vat_payer: form.vat_payer, vat_rate: form.vat_payer ? String(form.vat_rate).replace(',', '.') : null,
        valid_from: form.valid_from, note: form.note,
      });
      setMsg({ type: 'success', text: `Сохранено. Прибыль ${meta.label} пересчитана с ${formatDate(form.valid_from)}.` });
      await load();
    } catch (err) {
      setMsg({ type: 'error', text: apiError(err) });
    } finally {
      setSaving(false);
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!items) return <Skeleton variant="rounded" height={320} sx={{ borderRadius: '16px' }} />;

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12} lg={readOnly ? 12 : 4}>
        <Card sx={{ height: '100%', background: tokens.heroGradient }}>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.7)', color: tokens.primaryDark, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ReceiptLongOutlinedIcon />
              </Box>
              <Typography variant="h3">Сейчас действует</Typography>
            </Stack>
            {current ? (
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Налоговый режим</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.25rem' }}>{regimeLabel(current.regime)} · {pct(current.rate)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">НДС</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.25rem' }}>{current.vat_payer ? pct(current.vat_rate) : 'не плательщик'}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">с {formatDate(current.valid_from)}</Typography>
                {current.computed
                  ? <Chip label="налог входит в чистую прибыль" size="small" sx={{ alignSelf: 'flex-start', bgcolor: tokens.successSoft, color: tokens.success }} />
                  : <Chip label="прибыль показывается до налогов" size="small" sx={{ alignSelf: 'flex-start', bgcolor: tokens.warningSoft, color: tokens.warning }} />}
              </Stack>
            ) : (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', mb: 0.5 }}>Налоги не заданы</Typography>
                <Typography variant="body2" color="text.secondary">Пока налоги не указаны, прибыль {meta.label} показывается до налогов.</Typography>
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      {!readOnly && (
      <Grid item xs={12} lg={8}>
        <Card component="form" onSubmit={submit} noValidate sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h3" sx={{ mb: 0.5 }}>{current ? 'Изменить налог и НДС' : 'Указать налог и НДС'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Только для {meta.label}. Для других площадок налоги задаются в их разделах.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
              <TextField select label="Режим налогообложения" value={form.regime}
                onChange={(e) => { const r = REGIMES.find((x) => x.value === e.target.value); set({ regime: e.target.value, rate: r?.rate || form.rate }); }}>
                {REGIMES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>
              <TextField label="Ставка налога" value={form.rate} onChange={(e) => set({ rate: e.target.value })}
                error={Boolean(errors.rate)} helperText={errors.rate || ' '} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} inputProps={{ inputMode: 'decimal' }} />
              <TextField type="date" label="Действует с" value={form.valid_from} onChange={(e) => set({ valid_from: e.target.value })}
                InputLabelProps={{ shrink: true }} error={Boolean(errors.valid_from)} helperText={errors.valid_from || ' '} />
              {computed && (
                <TextField select label="С чего считать налог" value={form.income_base} onChange={(e) => set({ income_base: e.target.value })} sx={{ gridColumn: { md: 'span 2' } }}>
                  {BASES.map((b) => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                </TextField>
              )}
            </Box>

            <Box sx={{ mt: 1, p: 2, borderRadius: '12px', border: `1px solid ${tokens.border}`, bgcolor: form.vat_payer ? tokens.primaryTint : 'transparent' }}>
              <FormControlLabel control={<Switch checked={form.vat_payer} onChange={(e) => set({ vat_payer: e.target.checked })} />}
                label={<Typography sx={{ fontWeight: 600 }}>Плательщик НДС</Typography>} />
              {form.vat_payer && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mt: 1 }}>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {VAT_RATES.map((r) => (
                      <Chip key={r} label={`${r}%`} onClick={() => set({ vat_rate: r })}
                        sx={{ fontWeight: 700, bgcolor: form.vat_rate === r ? tokens.primary : tokens.paper, color: form.vat_rate === r ? '#fff' : tokens.ink,
                          border: `1px solid ${form.vat_rate === r ? tokens.primary : tokens.borderStrong}`, '&:hover': { bgcolor: form.vat_rate === r ? tokens.primaryDark : tokens.primarySoft } }} />
                    ))}
                  </Stack>
                  <TextField size="small" label="Своя ставка" value={form.vat_rate} onChange={(e) => set({ vat_rate: e.target.value })} sx={{ width: 140 }}
                    error={Boolean(errors.vat_rate)} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} inputProps={{ inputMode: 'decimal' }} />
                </Stack>
              )}
            </Box>

            <TextField fullWidth label="Комментарий" value={form.note} onChange={(e) => set({ note: e.target.value })} sx={{ mt: 2 }} inputProps={{ maxLength: 500 }}
              placeholder="Например: ИП Иванов, переход на НДС 5%" />
            {!computed && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Для режима «{regimeLabel(form.regime)}» MARQIT пока не вычитает налог из прибыли — нужна согласованная методика
                (учёт расходов, минимальный налог). Настройка сохранится, прибыль будет показана до налогов.
              </Alert>
            )}
            {msg && <Alert severity={msg.type} sx={{ mt: 2 }}>{msg.text}</Alert>}
            <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={!valid || saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
              Сохранить
            </Button>
          </CardContent>
        </Card>
      </Grid>
      )}

      {items.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 1.5 }}>Изменения по датам</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Действует с</TableCell>
                      <TableCell>Режим</TableCell>
                      <TableCell align="right">Ставка</TableCell>
                      <TableCell>База</TableCell>
                      <TableCell>НДС</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((t) => (
                      <TableRow key={t.id} hover>
                        <TableCell>{formatDate(t.valid_from)}</TableCell>
                        <TableCell>{regimeLabel(t.regime)}{t.note && <Typography variant="caption" color="text.secondary" display="block">{t.note}</Typography>}</TableCell>
                        <TableCell align="right">{pct(t.rate)}</TableCell>
                        <TableCell>{BASES.find((b) => b.value === t.income_base)?.label || '—'}</TableCell>
                        <TableCell>{t.vat_payer ? pct(t.vat_rate) : 'нет'}</TableCell>
                        <TableCell align="right">
                          {!readOnly && <Tooltip title="Удалить">
                            <IconButton size="small" onClick={() => setToDelete(t)} aria-label={`Удалить настройку с ${formatDate(t.valid_from)}`}>
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Удалить налоговую настройку?"
        text={toDelete ? `Настройка «${regimeLabel(toDelete.regime)}» с ${formatDate(toDelete.valid_from)} будет удалена. Прибыль за периоды, где она действовала, пересчитается.` : ''}
        confirmLabel="Удалить"
        danger
        onConfirm={() => settingsApi.deleteTax(toDelete.id)}
        onClose={(done) => { setToDelete(null); if (done) load(); }}
      />
    </Grid>
  );
};

export default TaxesPanel;
